class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.handleEscapeKeyUp = (event) => event.code === 'Escape' && this.close();
    this.handleDrawerClick = this.onDrawerClick.bind(this);
    this.handleOverlayClick = this.close.bind(this);
    this.handleDocumentCartTriggerClick = this.onDocumentCartTriggerClick.bind(this);
    this.handleDocumentCartTriggerKeydown = this.onDocumentCartTriggerKeydown.bind(this);
    this.cartScrollLockActive = false;

    this.addEventListener('keyup', this.handleEscapeKeyUp);
    this.addEventListener('click', this.handleDrawerClick);
    document.addEventListener('click', this.handleDocumentCartTriggerClick, true);
    document.addEventListener('keydown', this.handleDocumentCartTriggerKeydown, true);
    this.bindOverlayEvents();
    this.setHeaderCartIconAccessibility();
    this.initPpCartTagVisibilityGuard();
    this.syncGlobalCartUI({ item_count: this.getCartItemCountFromDOM() });
    this.fetchAndSyncCustomCartIndicators();

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        this.syncGlobalCartUI(event?.cartData);
      });
    }
  }

  disconnectedCallback() {
    this.cartUpdateUnsubscriber?.();
    this.removeEventListener('keyup', this.handleEscapeKeyUp);
    this.removeEventListener('click', this.handleDrawerClick);
    document.removeEventListener('click', this.handleDocumentCartTriggerClick, true);
    document.removeEventListener('keydown', this.handleDocumentCartTriggerKeydown, true);
    this.unlockPageScrollForCartDrawer();
  }

  bindOverlayEvents() {
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (!overlay) return;
    overlay.addEventListener('click', this.handleOverlayClick);
  }

  setHeaderCartIconAccessibility() {
    const cartLinks = document.querySelectorAll('#cart-icon-bubble, #cart-icon-bubble-desktop, [data-cart-trigger]');
    if (!cartLinks.length) return;

    cartLinks.forEach((cartLink) => {
      if (cartLink.dataset.cartDrawerBound === 'true') return;

      cartLink.dataset.cartDrawerBound = 'true';
      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // Defer class application to the next paint so transform/opacity transitions always run.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.classList.add('animate', 'active');
      });
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    this.lockPageScrollForCartDrawer();
  }

  close() {
    const focusTarget = this.activeElement;

    this.classList.remove('active');
    removeTrapFocus();
    this.unlockPageScrollForCartDrawer();

    if (focusTarget && typeof focusTarget.focus === 'function') {
      try {
        focusTarget.focus({ preventScroll: true });
      } catch (error) {}
    }
  }

  lockPageScrollForCartDrawer() {
    if (this.cartScrollLockActive) return;

    this.cartScrollLockActive = true;
    if (typeof window.__ppSetPageScrollLock === 'function') {
      window.__ppSetPageScrollLock('cart-drawer', true);
      return;
    }
    document.documentElement.classList.add('pp-scroll-locked');
    document.body.classList.add('pp-scroll-locked');
  }

  unlockPageScrollForCartDrawer() {
    if (!this.cartScrollLockActive) return;

    this.cartScrollLockActive = false;
    if (typeof window.__ppSetPageScrollLock === 'function') {
      window.__ppSetPageScrollLock('cart-drawer', false);
      return;
    }
    document.documentElement.classList.remove('pp-scroll-locked');
    document.body.classList.remove('pp-scroll-locked');
  }

  onDrawerClick(event) {
    const subscriptionUpgrade = event.target.closest('[data-subscription-upgrade]');
    if (subscriptionUpgrade) {
      event.preventDefault();
      this.replaceWithThreePack(subscriptionUpgrade);
      return;
    }

    const clearButton = event.target.closest('[data-clear-cart]');
    if (!clearButton) return;
    event.preventDefault();
    this.clearCart(clearButton);
  }

  async replaceWithThreePack(upgradeButton) {
    if (this.subscriptionUpgradeInProgress) return;

    const sourceLineKey = upgradeButton.dataset.lineKey;
    const targetVariantId = Number.parseInt(upgradeButton.dataset.targetVariantId || '', 10);
    const sellingPlanId = Number.parseInt(upgradeButton.dataset.sellingPlanId || '', 10);
    const replacementQuantity = 1;
    const errorElement = upgradeButton.querySelector('.cart-item__subscription-notice-error');

    if (
      !sourceLineKey ||
      !Number.isFinite(targetVariantId) ||
      targetVariantId <= 0 ||
      !Number.isFinite(sellingPlanId) ||
      sellingPlanId <= 0
    ) return;

    this.subscriptionUpgradeInProgress = true;
    upgradeButton.disabled = true;
    upgradeButton.setAttribute('aria-busy', 'true');
    if (errorElement) errorElement.textContent = '';

    let rollbackLineKey = null;
    let rollbackQuantity = 0;
    let upgradeCommitted = false;

    try {
      const cartResponse = await fetch(`${routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
      if (!cartResponse.ok) throw new Error(`Cart fetch failed: ${cartResponse.status}`);

      const currentCart = await cartResponse.json();
      const targetQuantitiesBefore = new Map(
        (currentCart.items || [])
          .filter((item) => Number(item.variant_id) === targetVariantId)
          .map((item) => [item.key, item.quantity])
      );
      const addBody = JSON.stringify({
        items: [
          {
            id: targetVariantId,
            quantity: replacementQuantity,
            selling_plan: sellingPlanId,
          },
        ],
      });
      const addResponse = await fetch(routes.cart_add_url, { ...fetchConfig(), body: addBody });
      const addedCartData = await addResponse.json();

      if (!addResponse.ok || addedCartData.errors || addedCartData.status) {
        throw new Error(addedCartData.description || addedCartData.message || 'Subscription add failed');
      }

      // The Ajax add response does not consistently include selling_plan_allocation.
      // Re-read the cart and verify the authoritative line-item state instead.
      const verificationResponse = await fetch(`${routes.cart_url}.js`, {
        cache: 'no-store',
        headers: { Accept: 'application/json' },
      });
      if (!verificationResponse.ok) {
        throw new Error(`Subscription verification failed: ${verificationResponse.status}`);
      }

      const verifiedCart = await verificationResponse.json();
      const changedVariantTarget = verifiedCart.items?.find(
        (item) =>
          Number(item.variant_id) === targetVariantId &&
          item.quantity > (targetQuantitiesBefore.get(item.key) || 0)
      );
      const addedTarget = verifiedCart.items?.find(
        (item) =>
          Number(item.variant_id) === targetVariantId &&
          Number(item.selling_plan_allocation?.selling_plan?.id) === sellingPlanId &&
          item.quantity >= (targetQuantitiesBefore.get(item.key) || 0) + replacementQuantity
      );
      rollbackLineKey = changedVariantTarget?.key || null;
      rollbackQuantity = rollbackLineKey ? targetQuantitiesBefore.get(rollbackLineKey) || 0 : 0;

      if (!addedTarget?.selling_plan_allocation) {
        throw new Error('The 3-Pack was not added with a subscription plan');
      }

      if (addedTarget.quantity !== replacementQuantity) {
        const normalizeBody = JSON.stringify({ id: addedTarget.key, quantity: replacementQuantity });
        const normalizeResponse = await fetch(routes.cart_change_url, { ...fetchConfig(), body: normalizeBody });
        const normalizedCart = await normalizeResponse.json();

        if (!normalizeResponse.ok || normalizedCart.errors || normalizedCart.status) {
          throw new Error(normalizedCart.description || normalizedCart.message || 'Subscription quantity reset failed');
        }
      }

      const removeBody = JSON.stringify({
        id: sourceLineKey,
        quantity: 0,
        sections: this.getSectionsToRender().map((section) => section.id),
        sections_url: window.location.pathname,
      });
      const removeResponse = await fetch(routes.cart_change_url, { ...fetchConfig(), body: removeBody });
      const updatedCart = await removeResponse.json();

      if (!removeResponse.ok || updatedCart.errors || updatedCart.status) {
        throw new Error(updatedCart.description || updatedCart.message || 'Original item removal failed');
      }

      upgradeCommitted = true;
      this.renderContents(updatedCart);
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'cart-drawer-subscription-upgrade',
          cartData: updatedCart,
          variantId: targetVariantId,
        });
      }
    } catch (error) {
      console.error(error);
      if (!upgradeCommitted && rollbackLineKey) {
        try {
          const rollbackBody = JSON.stringify({ id: rollbackLineKey, quantity: rollbackQuantity });
          await fetch(routes.cart_change_url, { ...fetchConfig(), body: rollbackBody });
        } catch (rollbackError) {
          console.error('Subscription upgrade rollback failed', rollbackError);
        }
      }
      if (errorElement) errorElement.textContent = 'Unable to update your cart. Please try again.';
      upgradeButton.disabled = false;
      upgradeButton.removeAttribute('aria-busy');
    } finally {
      this.subscriptionUpgradeInProgress = false;
    }
  }

  onDocumentCartTriggerClick(event) {
    const cartLink = event.target.closest('#cart-icon-bubble, #cart-icon-bubble-desktop, [data-cart-trigger]');
    if (!cartLink) return;
    event.preventDefault();
    this.open(cartLink);
  }

  onDocumentCartTriggerKeydown(event) {
    const cartLink = event.target.closest('#cart-icon-bubble, #cart-icon-bubble-desktop, [data-cart-trigger]');
    if (!cartLink) return;
    const code = (event.code || event.key || '').toUpperCase();
    if (code !== 'SPACE' && code !== 'ENTER' && code !== ' ') return;
    event.preventDefault();
    this.open(cartLink);
  }

  async clearCart(clearButton) {
    if (this.clearCartInProgress) return;

    this.clearCartInProgress = true;
    clearButton?.setAttribute('disabled', 'disabled');

    try {
      const clearResponse = await fetch('/cart/clear.js', { method: 'POST', headers: { Accept: 'application/json' } });
      if (!clearResponse.ok) {
        throw new Error(`Cart clear failed: ${clearResponse.status}`);
      }

      const sectionQuerySuffix = `t=${Date.now()}`;
      const [cartStateResponse, drawerSectionResponse, iconSectionResponse] = await Promise.all([
        fetch(`${routes.cart_url}.js`, { headers: { Accept: 'application/json' } }),
        fetch(`${routes.cart_url}?section_id=cart-drawer&${sectionQuerySuffix}`, {
          headers: { Accept: 'text/html' },
        }),
        fetch(`${routes.cart_url}?section_id=cart-icon-bubble&${sectionQuerySuffix}`, {
          headers: { Accept: 'text/html' },
        }),
      ]);

      const cartState = await cartStateResponse.json();
      const drawerSectionHTML = await drawerSectionResponse.text();
      const iconSectionHTML = await iconSectionResponse.text();

      this.renderClearedCartState(cartState, drawerSectionHTML, iconSectionHTML);

      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-drawer-clear', cartData: cartState });
      }
    } catch (error) {
      console.error(error);
    } finally {
      this.clearCartInProgress = false;
      clearButton?.removeAttribute('disabled');
    }
  }

  renderClearedCartState(cartState, drawerSectionHTML, iconSectionHTML) {
    const drawerDocument = new DOMParser().parseFromString(drawerSectionHTML, 'text/html');
    const nextDrawer = drawerDocument.querySelector('#CartDrawer');
    const currentDrawer = this.querySelector('#CartDrawer');

    if (currentDrawer && nextDrawer) {
      currentDrawer.innerHTML = nextDrawer.innerHTML;
    }

    const iconDocument = new DOMParser().parseFromString(iconSectionHTML, 'text/html');
    const nextIconSection = iconDocument.getElementById('shopify-section-cart-icon-bubble');
    const currentIconSection = document.getElementById('shopify-section-cart-icon-bubble');

    if (currentIconSection && nextIconSection) {
      currentIconSection.replaceWith(nextIconSection);
    } else {
      const nextBubble = iconDocument.getElementById('cart-icon-bubble');
      const currentBubble = document.getElementById('cart-icon-bubble');
      if (currentBubble && nextBubble) {
        currentBubble.innerHTML = nextBubble.innerHTML;
      }
    }

    this.classList.toggle('is-empty', this.getItemCount(cartState) === 0);
    this.setHeaderCartIconAccessibility();
    this.bindOverlayEvents();
    this.syncGlobalCartUI(cartState);
    this.open();
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    const itemCount = this.getItemCount(parsedState);
    const isEmpty = itemCount === 0;
    this.classList.toggle('is-empty', isEmpty);
    this.productId = parsedState.id;

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement || !parsedState?.sections?.[section.id]) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    this.setHeaderCartIconAccessibility();
    this.bindOverlayEvents();
    this.syncGlobalCartUI(parsedState);

    setTimeout(() => {
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const target = parsedHTML.querySelector(selector);
    return target ? target.innerHTML : '';
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  getCartItemCountFromDOM() {
    const bubbles = Array.from(document.querySelectorAll('[data-cart-count-bubble]'));
    if (!bubbles.length) return null;

    const visibleBubble = bubbles.find((bubble) => !bubble.hasAttribute('hidden'));
    if (visibleBubble) {
      const parsedCount = Number.parseInt(visibleBubble.textContent || '', 10);
      if (!Number.isNaN(parsedCount)) return parsedCount;
    }

    if (bubbles.every((bubble) => bubble.hasAttribute('hidden'))) return 0;
    return null;
  }

  syncGlobalCartUI(cartData) {
    this.syncCustomCartIndicators(cartData);
    this.syncPpCartTagVisibility(cartData);
  }

  initPpCartTagVisibilityGuard() {
    if (window.__ppCartTagEmptyGuardState) {
      this.ppCartTagGuardState = window.__ppCartTagEmptyGuardState;
      this.ppCartTagGuardState.apply();
      return;
    }

    const state = {
      observedTag: null,
      tagStyleObserver: null,
      bodyObserver: null,
      isApplying: false,
      isCartEmpty: null,
    };

    const hiddenStyleEntries = [
      ['display', 'none'],
      ['visibility', 'hidden'],
      ['opacity', '0'],
      ['pointer-events', 'none'],
      ['transform', 'translateX(120%)'],
    ];

    const clearEmptyHiddenStyles = (tag) => {
      hiddenStyleEntries.forEach(([property]) => {
        tag.style.removeProperty(property);
      });
      tag.dataset.plusCartEmptyHidden = 'false';
    };

    const applyEmptyHiddenStyles = (tag) => {
      hiddenStyleEntries.forEach(([property, value]) => {
        tag.style.setProperty(property, value, 'important');
      });
      tag.dataset.plusCartEmptyHidden = 'true';
    };

    const isHardHiddenContext = (tag) =>
      document.body.classList.contains('template-cart') ||
      tag.dataset.plusCartPageHidden === 'true' ||
      tag.dataset.plusProductMobileHidden === 'true';

    const connectTagObserver = (tag) => {
      if (state.observedTag === tag) return;
      state.observedTag = tag;

      if (state.tagStyleObserver) state.tagStyleObserver.disconnect();
      state.tagStyleObserver = new MutationObserver(() => {
        if (state.isApplying) return;
        state.apply();
      });
      state.tagStyleObserver.observe(tag, {
        attributes: true,
        attributeFilter: ['style', 'class', 'data-plus-cart-page-hidden', 'data-plus-product-mobile-hidden'],
      });
    };

    state.apply = () => {
      const tag = document.getElementById('pp-cart-tag');
      if (!tag) {
        state.observedTag = null;
        if (state.tagStyleObserver) state.tagStyleObserver.disconnect();
        return;
      }

      connectTagObserver(tag);

      if (state.isCartEmpty !== true) {
        if (tag.dataset.plusCartEmptyHidden === 'true' && !isHardHiddenContext(tag)) {
          state.isApplying = true;
          clearEmptyHiddenStyles(tag);
          state.isApplying = false;
        }
        return;
      }

      state.isApplying = true;
      applyEmptyHiddenStyles(tag);
      state.isApplying = false;
    };

    if (!state.bodyObserver && document.body) {
      state.bodyObserver = new MutationObserver(() => {
        state.apply();
      });
      state.bodyObserver.observe(document.body, {
        childList: true,
        subtree: true,
      });
    }

    window.__ppCartTagEmptyGuardState = state;
    this.ppCartTagGuardState = state;
    state.apply();
  }

  syncPpCartTagVisibility(cartData) {
    if (!this.ppCartTagGuardState) this.initPpCartTagVisibilityGuard();
    const itemCount = this.getItemCount(cartData);
    if (itemCount === null || !this.ppCartTagGuardState) return;
    this.ppCartTagGuardState.isCartEmpty = itemCount === 0;
    this.ppCartTagGuardState.apply();
  }

  syncCustomCartIndicators(cartData) {
    const count = this.getItemCount(cartData);

    if (count === null) {
      this.fetchAndSyncCustomCartIndicators();
      return;
    }
    const desktopCart = document.getElementById('cart-icon-bubble-desktop');
    if (desktopCart) {
      const countBubble = desktopCart.querySelector('[data-cart-count-bubble]');
      const countText = desktopCart.querySelector('[data-cart-count-text]');
      const emptyIcon = desktopCart.querySelector('[data-cart-icon-empty]');
      const fullIcon = desktopCart.querySelector('[data-cart-icon-full]');

      if (countBubble) {
        countBubble.hidden = count === 0;
        countBubble.textContent = count < 100 ? `${count}` : '99+';
      }

      if (countText) {
        countText.textContent = `${count} item${count === 1 ? '' : 's'} in cart`;
      }

      if (emptyIcon) emptyIcon.hidden = count > 0;
      if (fullIcon) fullIcon.hidden = count === 0;
    }

    this.syncDefaultCartBubble(count);
  }

  syncDefaultCartBubble(count) {
    const defaultCartLink = document.getElementById('cart-icon-bubble');
    if (!defaultCartLink) return;

    let bubble = defaultCartLink.querySelector('.cart-count-bubble');

    if (count <= 0) {
      if (bubble) bubble.remove();
      return;
    }

    if (!bubble) {
      bubble = document.createElement('div');
      bubble.className = 'cart-count-bubble';
      bubble.innerHTML = '<span aria-hidden="true"></span><span class="visually-hidden"></span>';
      defaultCartLink.appendChild(bubble);
    }

    const numberNode = bubble.querySelector('span[aria-hidden="true"]');
    const labelNode = bubble.querySelector('.visually-hidden');
    if (numberNode) {
      numberNode.textContent = count < 100 ? `${count}` : '';
    }
    if (labelNode) {
      labelNode.textContent = `${count} item${count === 1 ? '' : 's'} in cart`;
    }
  }

  fetchAndSyncCustomCartIndicators() {
    if (this.cartCountSyncInFlight) return this.cartCountSyncInFlight;

    this.cartCountSyncInFlight = fetch(`${routes.cart_url}.js`, {
      headers: { Accept: 'application/json' },
    })
      .then((response) => response.json())
      .then((cartState) => this.syncGlobalCartUI(cartState))
      .catch((error) => console.error(error))
      .finally(() => {
        this.cartCountSyncInFlight = null;
      });

    return this.cartCountSyncInFlight;
  }

  getItemCount(cartData) {
    if (!cartData) return null;
    if (typeof cartData.item_count === 'number') return cartData.item_count;
    if (Array.isArray(cartData.items)) {
      return cartData.items.reduce((total, item) => total + (Number(item?.quantity) || 0), 0);
    }
    return null;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
